import { useState, useMemo } from "react";
import "@/App.css";
import { Calculator, TrendingUp } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// Format currency in Indian numbering system
const formatINR = (value) => {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)}Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)}L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  }
  return `₹${value.toLocaleString("en-IN")}`;
};

const formatINRFull = (value) => {
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
};

// Custom Tooltip for Chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-gray-800 mb-1">Year {label}</p>
        <p className="text-sm text-gray-600">
          Invested: {formatINRFull(payload[0]?.value || 0)}
        </p>
        <p className="text-sm text-emerald-600 font-medium">
          Value: {formatINRFull(payload[1]?.value || 0)}
        </p>
      </div>
    );
  }
  return null;
};

function App() {
  // Slider states
  const [monthlyInstallment, setMonthlyInstallment] = useState(3000);
  const [paymentPeriod, setPaymentPeriod] = useState(2);
  const [holdingPeriod, setHoldingPeriod] = useState(2);
  const [annualReturn, setAnnualReturn] = useState(12);
  const [initialLumpSum, setInitialLumpSum] = useState(0);

  // Ensure holding period is at least equal to payment period
  const effectiveHoldingPeriod = Math.max(holdingPeriod, paymentPeriod);

  // Calculate investment values
  const calculations = useMemo(() => {
    const monthlyRate = annualReturn / 100 / 12;
    const totalMonths = paymentPeriod * 12;
    const totalInvested = initialLumpSum + monthlyInstallment * totalMonths;

    // Calculate SIP future value at end of payment period
    let sipValue = 0;
    if (monthlyRate > 0) {
      sipValue =
        monthlyInstallment *
        (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
          (1 + monthlyRate));
    } else {
      sipValue = monthlyInstallment * totalMonths;
    }

    // Add lump sum growth during payment period
    const lumpSumAtPaymentEnd =
      initialLumpSum * Math.pow(1 + annualReturn / 100, paymentPeriod);
    const valueAtPaymentEnd = sipValue + lumpSumAtPaymentEnd;

    // Compound growth during holding period (after payments stop)
    const additionalHoldingYears = effectiveHoldingPeriod - paymentPeriod;
    const finalValue =
      valueAtPaymentEnd * Math.pow(1 + annualReturn / 100, additionalHoldingYears);

    const returnsEarned = finalValue - totalInvested;

    // Generate chart data (year by year)
    const chartData = [];
    for (let year = 1; year <= effectiveHoldingPeriod; year++) {
      const monthsCompleted = Math.min(year * 12, totalMonths);
      const investedSoFar = initialLumpSum + monthlyInstallment * monthsCompleted;

      let valueAtYear;
      if (year <= paymentPeriod) {
        // Still in payment period
        let sipValueAtYear = 0;
        if (monthlyRate > 0) {
          sipValueAtYear =
            monthlyInstallment *
            (((Math.pow(1 + monthlyRate, monthsCompleted) - 1) / monthlyRate) *
              (1 + monthlyRate));
        } else {
          sipValueAtYear = monthlyInstallment * monthsCompleted;
        }
        const lumpSumAtYear =
          initialLumpSum * Math.pow(1 + annualReturn / 100, year);
        valueAtYear = sipValueAtYear + lumpSumAtYear;
      } else {
        // After payment period - compound the value at end of payment period
        const yearsAfterPayment = year - paymentPeriod;
        valueAtYear =
          valueAtPaymentEnd * Math.pow(1 + annualReturn / 100, yearsAfterPayment);
      }

      chartData.push({
        year,
        invested: Math.round(investedSoFar),
        value: Math.round(valueAtYear),
      });
    }

    return {
      totalInvested,
      finalValue: Math.round(finalValue),
      returnsEarned: Math.round(returnsEarned),
      chartData,
    };
  }, [
    monthlyInstallment,
    paymentPeriod,
    effectiveHoldingPeriod,
    annualReturn,
    initialLumpSum,
  ]);

  return (
    <div className="min-h-screen bg-[#F4F5F0]" data-testid="growth-planner-app">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-200">
            <Calculator className="w-6 h-6 text-emerald-800" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1A2E25] font-heading">
            Growth Planner
          </h1>
        </div>
        <p className="text-[#4A5D54] mb-8 font-body">
          Visualize your wealth journey with monthly investments
        </p>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Parameters Card */}
          <div className="lg:col-span-7 bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-[#E2E8F0]">
            <h2 className="text-xl font-bold text-[#1A2E25] mb-6 font-heading">
              Investment Parameters
            </h2>

            <div className="space-y-8">
              {/* Monthly Installment */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-[#4A5D54] uppercase tracking-wider font-body">
                    Monthly Installment
                  </label>
                  <span
                    className="text-lg font-bold text-[#1A2E25] bg-[#F4F5F0] px-3 py-1 rounded-md font-body"
                    data-testid="value-monthly-installment"
                  >
                    {monthlyInstallment.toLocaleString("en-IN")}
                  </span>
                </div>
                <Slider
                  data-testid="slider-monthly-installment"
                  value={[monthlyInstallment]}
                  onValueChange={(val) => setMonthlyInstallment(val[0])}
                  min={500}
                  max={100000}
                  step={500}
                  className="slider-custom"
                />
                <div className="flex justify-between text-xs text-[#4A5D54] mt-2 font-body">
                  <span>₹500</span>
                  <span>₹1,00,000</span>
                </div>
              </div>

              {/* Payment Period */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-[#4A5D54] uppercase tracking-wider font-body">
                    Payment Period (Years)
                  </label>
                  <span
                    className="text-lg font-bold text-[#1A2E25] bg-[#F4F5F0] px-3 py-1 rounded-md font-body"
                    data-testid="value-payment-period"
                  >
                    {paymentPeriod}
                  </span>
                </div>
                <Slider
                  data-testid="slider-payment-period"
                  value={[paymentPeriod]}
                  onValueChange={(val) => {
                    setPaymentPeriod(val[0]);
                    if (holdingPeriod < val[0]) {
                      setHoldingPeriod(val[0]);
                    }
                  }}
                  min={1}
                  max={30}
                  step={1}
                  className="slider-custom"
                />
                <div className="flex justify-between text-xs text-[#4A5D54] mt-2 font-body">
                  <span>1 year</span>
                  <span>30 years</span>
                </div>
              </div>

              {/* Total Holding Period */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-[#4A5D54] uppercase tracking-wider font-body">
                    Total Holding Period (Years)
                  </label>
                  <span
                    className="text-lg font-bold text-[#1A2E25] bg-[#F4F5F0] px-3 py-1 rounded-md font-body"
                    data-testid="value-holding-period"
                  >
                    {effectiveHoldingPeriod}
                  </span>
                </div>
                <Slider
                  data-testid="slider-holding-period"
                  value={[holdingPeriod]}
                  onValueChange={(val) => setHoldingPeriod(val[0])}
                  min={paymentPeriod}
                  max={40}
                  step={1}
                  className="slider-custom"
                />
                <div className="flex justify-between text-xs text-[#4A5D54] mt-2 font-body">
                  <span>{paymentPeriod} years</span>
                  <span>40 years</span>
                </div>
              </div>

              {/* Expected Annual Return */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-[#4A5D54] uppercase tracking-wider font-body">
                    Expected Annual Return
                  </label>
                  <span
                    className="text-lg font-bold text-[#1A2E25] bg-[#F4F5F0] px-3 py-1 rounded-md font-body"
                    data-testid="value-annual-return"
                  >
                    {annualReturn}
                  </span>
                </div>
                <Slider
                  data-testid="slider-annual-return"
                  value={[annualReturn]}
                  onValueChange={(val) => setAnnualReturn(val[0])}
                  min={1}
                  max={30}
                  step={0.5}
                  className="slider-custom"
                />
                <div className="flex justify-between text-xs text-[#4A5D54] mt-2 font-body">
                  <span>1%</span>
                  <span>30%</span>
                </div>
              </div>

              {/* Initial Lump Sum */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-[#4A5D54] uppercase tracking-wider font-body">
                    Initial Lump Sum (Optional)
                  </label>
                  <span
                    className="text-lg font-bold text-[#1A2E25] bg-[#F4F5F0] px-3 py-1 rounded-md font-body"
                    data-testid="value-initial-lump-sum"
                  >
                    {initialLumpSum.toLocaleString("en-IN")}
                  </span>
                </div>
                <Slider
                  data-testid="slider-initial-lump-sum"
                  value={[initialLumpSum]}
                  onValueChange={(val) => setInitialLumpSum(val[0])}
                  min={0}
                  max={1000000}
                  step={10000}
                  className="slider-custom"
                />
                <div className="flex justify-between text-xs text-[#4A5D54] mt-2 font-body">
                  <span>₹0</span>
                  <span>₹10,00,000</span>
                </div>
              </div>
            </div>
          </div>

          {/* Results Card */}
          <div className="lg:col-span-5 bg-gradient-to-br from-emerald-900 to-teal-950 rounded-2xl p-6 md:p-8 text-white shadow-xl flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-teal-300" />
              <span className="text-teal-200/80 text-sm uppercase tracking-widest font-body">
                Final Investment Value
              </span>
            </div>

            <h3
              className="text-4xl md:text-5xl font-bold text-white mb-1 tracking-tight font-heading"
              data-testid="result-final-value"
            >
              {formatINRFull(calculations.finalValue)}
            </h3>
            <p className="text-teal-200/70 text-sm mb-8 font-body">
              After {effectiveHoldingPeriod} years
            </p>

            <div className="border-t border-teal-800/50 pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-teal-200/70 text-sm mb-1 font-body">
                    Total Invested
                  </p>
                  <p
                    className="text-2xl font-semibold text-teal-50 font-heading"
                    data-testid="result-total-invested"
                  >
                    {formatINRFull(calculations.totalInvested)}
                  </p>
                </div>
                <div>
                  <p className="text-teal-200/70 text-sm mb-1 font-body">
                    Returns Earned
                  </p>
                  <p
                    className="text-2xl font-semibold text-emerald-400 font-heading"
                    data-testid="result-returns-earned"
                  >
                    {formatINRFull(calculations.returnsEarned)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Card */}
        <div
          className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-[#E2E8F0] mt-8"
          data-testid="growth-chart"
        >
          <h2 className="text-xl font-bold text-[#1A2E25] mb-6 font-heading">
            Investment Growth Over Time
          </h2>

          <div className="h-64 md:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={calculations.chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="year"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#4A5D54", fontSize: 12 }}
                  label={{
                    value: "Years",
                    position: "bottom",
                    offset: -5,
                    fill: "#4A5D54",
                    fontSize: 14,
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#4A5D54", fontSize: 12 }}
                  tickFormatter={formatINR}
                  width={70}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                  formatter={(value) => (
                    <span className="text-sm text-[#4A5D54] font-body">
                      {value === "invested" ? "Invested Amount" : "Total Value"}
                    </span>
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="invested"
                  stroke="#94A3B8"
                  strokeWidth={2}
                  fill="transparent"
                  name="invested"
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#10B981"
                  strokeWidth={3}
                  fill="url(#colorValue)"
                  name="value"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
